import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import SEO from "../components/SEO";
import MagneticButton from "../components/MagneticButton";
import { trackFormSubmission, trackEmailClick, trackServiceInterest } from "../utils/analytics";
import "./Contact.css";

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    storeUrl: '',
    serviceInterest: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState(null); // 'success', 'error', null
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Track service interest selection
    if (name === 'serviceInterest' && value) {
      trackServiceInterest(value);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Shake animation on error
      const form = e.target;
      form.style.animation = 'shake 0.5s';
      setTimeout(() => {
        form.style.animation = '';
      }, 500);
      return;
    }

    // Initialize EmailJS (only needs to be done once, but safe to call multiple times)
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

    // Check if EmailJS is configured
    if (serviceId === 'YOUR_SERVICE_ID' || templateId === 'YOUR_TEMPLATE_ID' || publicKey === 'YOUR_PUBLIC_KEY') {
      // Fallback: Open email client with pre-filled data
      const subject = encodeURIComponent(`Contact Form: ${formData.serviceInterest || 'General Inquiry'}`);
      const body = encodeURIComponent(
        `Name: ${formData.name}\n` +
        `Email: ${formData.email}\n` +
        `Store URL: ${formData.storeUrl || 'N/A'}\n` +
        `Service Interest: ${formData.serviceInterest || 'N/A'}\n\n` +
        `Message:\n${formData.message}`
      );
      // Track email fallback as conversion
      trackFormSubmission('contact_email_fallback', formData);
      
      window.location.href = `mailto:daniel@wavetech.solutions?subject=${subject}&body=${body}`;
      setFormStatus('success');
      setTimeout(() => {
        setFormStatus(null);
        setFormData({ name: '', email: '', storeUrl: '', serviceInterest: '', message: '' });
      }, 3000);
      return;
    }

    // Send email via EmailJS
    try {
      setFormStatus(null); // Clear any previous status
      
      await emailjs.send(
        serviceId,
        templateId,
        {
          name: formData.name,
          email: formData.email,
          reply_to: formData.email,
          title: formData.serviceInterest || 'General Inquiry',
          store_url: formData.storeUrl || 'Not provided',
          service_interest: formData.serviceInterest || 'General Inquiry',
          message: formData.message,
          to_email: 'daniel@wavetech.solutions',
        },
        publicKey
      );

      setFormStatus('success');
      
      // Track successful form submission as conversion
      trackFormSubmission('contact', formData);
      
      setFormData({ name: '', email: '', storeUrl: '', serviceInterest: '', message: '' });
      
      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setFormStatus('error');
      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    }
  };

  return (
    <>
      <SEO />
      <main className="contact-page">
        <section className="contact-hero">
          <div className="container">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="contact-hero-content"
            >
              <h1>Work With WaveTech</h1>
              <p className="hero-subtitle">
                We take on a small number of engagements at a time. Tell us what you want to build and why and we&apos;ll let you know if it&apos;s a fit.
              </p>
            </motion.div>
          </div>
        </section>

        {/* TRUST SIGNALS */}
        <section className="trust-signals">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="trust-badges"
            >
              <motion.div variants={fadeIn} className="trust-badge">
                <span className="badge-icon">⚡</span>
                <div>
                  <strong>Average response time: 4 hours</strong>
                  <p>We respond quickly to all inquiries</p>
                </div>
              </motion.div>
              <motion.div variants={fadeIn} className="trust-badge">
                <span className="badge-icon">🕐</span>
                <div>
                  <strong>Business hours: Mon-Fri 9am-6pm PST</strong>
                  <p>Available during business hours</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* CONTACT FORM SECTION */}
        <section className="contact-form-section">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="form-container"
            >
              <h2>Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                >
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? 'error' : ''}
                    required
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    required
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <label htmlFor="storeUrl">Store URL (Optional)</label>
                  <input
                    type="url"
                    id="storeUrl"
                    name="storeUrl"
                    value={formData.storeUrl}
                    onChange={handleChange}
                    placeholder="https://yourstore.myshopify.com"
                  />
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <label htmlFor="serviceInterest">Service Interest</label>
                  <select
                    id="serviceInterest"
                    name="serviceInterest"
                    value={formData.serviceInterest}
                    onChange={handleChange}
                  >
                    <option value="">Select a service...</option>
                    <option value="maintenance">Maintenance & Upgrades</option>
                    <option value="seo">Technical SEO & Performance</option>
                    <option value="development">Custom Development</option>
                    <option value="consulting">Ecommerce Consulting</option>
                    <option value="app-support">App Support</option>
                    <option value="other">Other</option>
                  </select>
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                >
                  <label htmlFor="message">What problem are you trying to solve? (plain English) *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    value={formData.message}
                    onChange={handleChange}
                    className={errors.message ? 'error' : ''}
                    required
                  />
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                >
                  <MagneticButton
                    type="submit"
                    className="submit-button"
                  >
                    Send Message
                  </MagneticButton>
                </motion.div>

                {formStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="form-message success"
                  >
                    ✓ Message sent successfully! We'll get back to you soon.
                  </motion.div>
                )}

                {formStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="form-message error"
                  >
                    ✗ Something went wrong. Please try again or email us directly.
                  </motion.div>
                )}
              </form>
            </motion.div>
          </div>
        </section>

        {/* SUPPORT PORTAL */}
        <section className="email-contact-section">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="email-box"
            >
              <h2>App support &amp; bugs</h2>
              <p>
                Using Wave Notify, Wave Sync, or another WaveTech app? Open a ticket on our support portal — we&apos;ll
                track your issue and reply by email.
              </p>
              <motion.a
                href="https://support.wavetech.solutions/support/new?type=general&project=WaveTech+Website"
                className="email-link"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Open support portal
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* EMAIL CONTACT SECTION */}
        <section className="email-contact-section">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="email-box"
            >
              <h2>Prefer Email?</h2>
              <p>Send us an email directly at:</p>
              <motion.a
                href="mailto:daniel@wavetech.solutions"
                className="email-link"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => trackEmailClick('daniel@wavetech.solutions', 'contact_page')}
              >
                daniel@wavetech.solutions
              </motion.a>
              <p className="response-time">We aim to respond within 1–2 business days.</p>
            </motion.div>
          </div>
        </section>

        {/* EXPANDED FAQ SECTION */}
        <section className="contact-faq-section">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <h2>Frequently Asked Questions</h2>
              
              <div className="faq-list">
                {[
                  {
                    q: "How do you structure pricing?",
                    a: "We offer fixed-rate or hourly models based on your project's scope. Email us for a custom quote."
                  },
                  {
                    q: "How long do website upgrades take?",
                    a: "Minor updates can be done in a day, while major overhauls can span weeks. We'll give a timeline after assessing your needs."
                  },
                  {
                    q: "Do you provide emergency support?",
                    a: "Yes, for critical app issues use the support portal and choose Emergency, or email daniel@wavetech.solutions with 'URGENT' in the subject line."
                  },
                  {
                    q: "Can you integrate custom apps with Shopify?",
                    a: "Absolutely. We specialize in custom-coded solutions for Shopify."
                  },
                  {
                    q: "Do you work with international clients?",
                    a: "Yes, we work with clients worldwide. All communication is in English, and we accommodate different time zones."
                  },
                  {
                    q: "What industries do you specialize in?",
                    a: "We work with ecommerce businesses across all industries including fashion, electronics, health & beauty, food & beverage, and B2B."
                  },
                  {
                    q: "Can you help with existing Shopify stores or only new builds?",
                    a: "We work with both existing stores and new builds. Most of our clients have existing stores that need optimization, maintenance, or new features."
                  },
                  {
                    q: "Do you offer white-label development services?",
                    a: "Yes, we offer white-label services for agencies. Contact us to discuss your specific needs."
                  }
                ].map((faq, idx) => (
                  <FAQItem key={idx} question={faq.q} answer={faq.a} delay={idx * 0.1} />
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}

// FAQ Accordion Component
const FAQItem = ({ question, answer, delay }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <motion.div
      className={`faq-item ${isOpen ? 'open' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
    >
      <button
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{question}</span>
        <motion.span
          className="faq-arrow"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          ▼
        </motion.span>
      </button>
      <motion.div
        className="faq-answer"
        initial={false}
        animate={{
          height: isOpen ? 'auto' : 0,
          opacity: isOpen ? 1 : 0,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <p>{answer}</p>
      </motion.div>
    </motion.div>
  );
};

export default Contact;
